import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaDownload, FaTimes, FaSpinner, FaExpand, FaCompress } from 'react-icons/fa';
import { Resizable } from 're-resizable';
import Draggable from 'react-draggable';
import './Report.css';

const DraggableReportModal = ({ reportId, onClose, onDownload }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modalRef = useRef(null);
  const nodeRef = useRef(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/reports/${reportId}`);
        setReport(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching report details:', err);
        setError('Failed to load report details');
        setLoading(false);
      }
    };

    if (reportId) {
      fetchReport();
    }
  }, [reportId]);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Close modal when clicking outside the content
  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('draggable-report-modal-backdrop')) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="draggable-report-modal-backdrop" onClick={handleBackdropClick}>
        <div className="draggable-report-modal-container">
          <div className="draggable-report-modal-content">
            <div className="draggable-report-modal-loading">
              <FaSpinner className="fa-spin" />
              <p>Loading report...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="draggable-report-modal-backdrop" onClick={handleBackdropClick}>
        <div className="draggable-report-modal-container">
          <div className="draggable-report-modal-content">
            <div className="draggable-report-modal-header">
              <h2>Error</h2>
              <button className="close-button" onClick={onClose}>
                <FaTimes />
              </button>
            </div>
            <div className="draggable-report-modal-body">
              <p className="error-message">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const modalContent = (
    <div className="draggable-report-modal-content" ref={modalRef}>
      <div className="draggable-report-modal-header" style={{ cursor: 'move' }}>
        <h2>{report.title || 'Report Details'}</h2>
        <div className="modal-header-actions">
          <button 
            className="download-button" 
            onClick={() => onDownload(reportId)}
            title="Download Report"
          >
            <FaDownload />
          </button>
          <button 
            className="fullscreen-button" 
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
          <button className="close-button" onClick={onClose} title="Close">
            <FaTimes />
          </button>
        </div>
      </div>
      
      <div className="draggable-report-modal-body">
        {report.type && (
          <div className="report-detail">
            <strong>Type:</strong> {report.type}
          </div>
        )}
        
        {report.createdAt && (
          <div className="report-detail">
            <strong>Date:</strong> {new Date(report.createdAt).toLocaleDateString()}
          </div>
        )}
        
        {report.description && (
          <div className="report-detail">
            <strong>Description:</strong>
            <p>{report.description}</p>
          </div>
        )}
        
        <div className="report-content">
          <h3>Report Content</h3>
          <div className="report-content-display">
            {report.content ? (
              (() => {
                try {
                  // First try to parse as JSON
                  const parsedContent = JSON.parse(report.content);
                  return <pre>{JSON.stringify(parsedContent, null, 2)}</pre>;
                } catch (e) {
                  // If not valid JSON, check if it's HTML
                  if (report.content.includes('<html>') || report.content.includes('<div') || report.content.includes('<p>')) {
                    return <div dangerouslySetInnerHTML={{ __html: report.content }} />;
                  } else {
                    // Otherwise treat as plain text
                    return <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{report.content}</pre>;
                  }
                }
              })()
            ) : (
              <div className="empty-content">
                <p style={{ color: '#4C5454', textAlign: 'center', padding: '20px' }}>
                  This report does not have any content. You can generate content by running an assessment.
                </p>
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <button 
                    onClick={onClose} 
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#243444',
                      color: '#D2BA7B',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="draggable-report-modal-backdrop" onClick={handleBackdropClick}>
      {isFullscreen ? (
        <div className="draggable-report-modal-fullscreen">
          {modalContent}
        </div>
      ) : (
        <Draggable 
          handle=".draggable-report-modal-header"
          defaultPosition={{x: window.innerWidth / 2 - 300, y: window.innerHeight / 2 - 200}}
          bounds="body"
          nodeRef={nodeRef}
        >
          <div ref={nodeRef} className="draggable-report-modal-container">
            <Resizable
              defaultSize={{
                width: 600,
                height: 500,
              }}
              minWidth={400}
              minHeight={300}
              maxWidth={window.innerWidth * 0.9}
              maxHeight={window.innerHeight * 0.9}
              handleStyles={{
                bottomRight: {
                  borderBottom: `8px solid #D2BA7B`,
                  borderRight: `8px solid #D2BA7B`,
                  height: 16,
                  width: 16,
                  right: 0,
                  bottom: 0,
                  cursor: 'nwse-resize'
                }
              }}
              enable={{
                top: false,
                right: false,
                bottom: false,
                left: false,
                topRight: false,
                bottomRight: true,
                bottomLeft: false,
                topLeft: false
              }}
            >
              <div style={{ width: '100%', height: '100%' }}>
                {modalContent}
              </div>
            </Resizable>
          </div>
        </Draggable>
      )}
    </div>
  );
};

export default DraggableReportModal;
